/**
 * contentRowActions
 *
 * Shared per-item kebab menu + row action runners for a content row (session,
 * partner note, guide, or practice — a SearchItem per `searchSources.js`'s
 * shape doc). Extracted from `SidebarRecentsController` so the dashboard's
 * Recent/Starred library rows (`DashboardManager.js`) can offer the SAME
 * kebab menu + actions as the sidebar's Recents rows, without a second
 * hand-copy drifting out of sync. `thread` items are sidebar-only (open
 * threads have no presence in the dashboard's `searchSources`/`PRACTICE_SOURCE`
 * set) but the thread branches are kept here since the sidebar still needs
 * them.
 *
 * Every runner (`runStar`/`runArchive`/`runMarkViewed`/`runToggleFlag`/
 * `runDelete`) patches the passed item in place (objects are references, so
 * a patch is visible to whichever list — the sidebar's `items` or the
 * dashboard's `libraryItems` — the item was pulled from) and broadcasts on
 * the shared `contentSync` bridge ONLY after a verified success — never patch
 * or emit on a falsy/null envelope or a `success === false` response
 * (R2-Critical-1). `runDelete` cannot patch a field (there is no
 * "deleted: true" flag on a SearchItem) — it performs the API call + emits on
 * success, and returns whether it succeeded so the caller can remove the item
 * from its OWN list (the only piece that can't be generalized here, since
 * each surface owns a differently-shaped array).
 *
 * @module components/contentRowActions
 */

import { API } from '../core/api.js';
import { eventBus } from '../core/utils.js';
import { discoveryApi } from './searchSources.js';
import {
  emitContentStarred,
  emitContentArchived,
  emitContentDeleted,
  emitContentViewed,
  emitContentFlagged
} from '../core/contentSync.js';
import { notifyArchived, notifyArchiveFailed } from '../utils/archiveFeedback.js';
import { modalService } from '../services/ModalService.js';
import { launchMemoDiscussAdvice } from '../orchestration/memoEntryLauncher.js';

/** Starred-row badge glyph. Deliberately carries no width/height attributes
 * (unlike every other sprite site in this codebase) because the badge is
 * CSS-sized by its consumer — sidebar.css's `--sb-recent-star` token pair for
 * `.sidebar-recent-row__icon-star`, dashboard.css's fixed size for
 * `.dashboard-recent-activity__item-icon-star`. */
export const STAR_ICON = `<svg viewBox="0 0 24 24" aria-hidden="true"><use href="#icon-star"/></svg>`;

/**
 * Resolve the shared unread presentation for a content row.
 * Sessions keep their dedicated manual `flaggedAt` persistence because their
 * native `viewedAt` auto-stamps on open; the distinction is intentionally
 * hidden at the presentation layer so every eligible row uses the same dot,
 * wording, and eye icons. Sent notes and threads have no recipient read state.
 * @param {Object} item - SearchItem
 * @returns {{unread: boolean, action: string, label: string, icon: string}|null}
 */
export function getRowUnreadState(item) {
  const isSentNote = item.type === 'note' && item.payload?.direction === 'sent';
  if (item.type === 'thread' || isSentNote) return null;

  const isSession = item.type === 'session';
  const unread = isSession ? Boolean(item.flagged) : !item.viewed;
  return {
    unread,
    action: isSession ? 'row-flag' : 'row-view',
    label: unread ? 'Mark as read' : 'Mark as unread',
    icon: unread ? 'eye' : 'eye-off'
  };
}

/**
 * Build a content row's kebab-menu items, in the order `renderMenuItems`
 * (optionsMenu.js) expects — it floats the discuss action to the top itself, so
 * callers hand it caller order and the RENDERED order comes out as: Discuss
 * (or Report back for an active experiment; omitted for sent notes and guides),
 * Star/Unstar, Mark as read/unread, Archive/Restore, Delete (sent notes omit
 * Discuss and read state). Experiment lifecycle items mirror `/experiments`: Archive for
 * active rows, Restore for archived rows, neither for completed rows, and
 * Delete for every status. Guides get NO Delete row on any surface — Archive
 * is the whole put-it-away vocabulary for an immutable written guide — so this
 * one omission covers the sidebar recents rows and the dashboard library rows.
 * @param {Object} item - SearchItem
 * @returns {Array<Object>} Menu items (see optionsMenu.js's `renderMenuItems`)
 */
export function buildContentRowMenuItems(item) {
  // A sent partner note has no "Discuss" edge of its own (see
  // partnerNotesDrillHelper: sent notes get "Revoke" as their primary
  // action, not "Discuss Note") — omit it here to match.
  const isSentNote = item.type === 'note' && item.payload?.direction === 'sent';
  const isPractice = item.type === 'practice';
  // Guides have no whole-document Discuss edge (matches SessionsSection's
  // /search kebab): a guide is discussed one atom at a time from its own
  // reading panel — clicking the row already opens that panel.
  const isGuide = item.type === 'guide';
  const items = [{ action: 'row-star', label: item.starred ? 'Unstar' : 'Star', icon: item.starred ? 'star' : 'star-outline' }];

  // Practices get a relabeled "Report back" (the generic Discuss dispatch,
  // mirrored to PracticesSection.handleDiscuss's own TREATMENT check-in
  // navigation — see `discussContentItem`), gated to active status exactly
  // like the native `/experiments` nav-card kebab, whose label this matches.
  if (isPractice) {
    if (item.payload?.status === 'active') {
      items.push({ action: 'row-discuss', label: 'Report back' });
    }
  } else if (!isSentNote && !isGuide) {
    items.push({ action: 'row-discuss', label: 'Discuss' });
  }
  const unreadState = getRowUnreadState(item);
  if (unreadState) items.push(unreadState);
  if (isPractice) {
    if (item.payload?.status === 'active') {
      items.push({ action: 'row-archive', label: 'Archive' });
    } else if (item.payload?.status === 'archived') {
      items.push({ action: 'row-restore', label: 'Restore' });
    }
    items.push({ action: 'row-delete', label: 'Delete', variant: 'danger' });
  } else {
    items.push({ action: 'row-archive', label: item.archived ? 'Unarchive' : 'Archive' });
    if (!isGuide) items.push({ action: 'row-delete', label: 'Delete', variant: 'danger' });
  }

  return items;
}

/**
 * The delete confirm-modal step, shared so every surface's "Delete" kebab
 * item shows identical copy.
 * @param {Object} item - SearchItem
 * @returns {Promise<boolean>} True if the user confirmed
 */
export function confirmDeleteContentItem(item) {
  const isPractice = item.type === 'practice';
  return modalService.confirm({
    title: isPractice ? 'Delete experiment' : 'Delete item',
    message: isPractice
      ? 'Permanently delete this experiment? This also deletes its reports.'
      : 'This item will be removed. This can’t be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger'
  });
}

/**
 * Star/unstar a single item, patching local state and broadcasting on
 * verified success only (see module doc — universal contentSync bridge).
 * `guide` is keyed by guideDefinitionId (`item.id` on this shape already IS
 * the guideDefinitionId — see searchSources.normalizeGuide), which is the
 * same key `/search` and the `/insights` guides tab broadcast on, so a guide
 * star/archive/delete here reaches both of those surfaces.
 * @param {Object} item - SearchItem
 * @returns {Promise<void>}
 */
export async function runStar(item) {
  const next = !item.starred;
  try {
    let res;
    if (item.type === 'session') {
      res = await API.starSessionNotes([item.id], next);
    } else if (item.type === 'thread') {
      res = await API.starOpenThreads([item.id], next);
    } else if (item.type === 'guide') {
      res = await API.starGuides([item.id], next);
    } else if (item.type === 'practice') {
      res = await API.starPractices([item.id], next);
    } else {
      res = await discoveryApi.starMemos([item.id], next);
    }
    // A falsy/null envelope is a swallowed failure — never patch or broadcast
    // a mutation the server didn't confirm (R2-Critical-1).
    if (!res || res.success === false) {
      console.error('[contentRowActions] Star action failed:', res?.error);
      return;
    }
    item.starred = next;
    item.starredAt = next ? new Date().toISOString() : null;
    if (item.payload) item.payload.starredAt = item.starredAt;
    emitContentStarred(item.type, item.id, next);
  } catch (err) {
    console.error('[contentRowActions] Star action failed:', err);
  }
}

/**
 * Archive/unarchive a single item, patching local state on success only.
 * @param {Object} item - SearchItem
 * @returns {Promise<void>}
 */
export async function runArchive(item) {
  const next = item.type === 'practice'
    ? item.payload?.status !== 'archived'
    : !item.archived;
  try {
    let res;
    if (item.type === 'session') {
      res = await API.archiveSessionNotes([item.id], next);
    } else if (item.type === 'thread') {
      res = await API.archiveOpenThreads([item.id], next);
    } else if (item.type === 'guide') {
      res = await API.archiveGuides([item.id], next);
    } else if (item.type === 'practice') {
      res = next
        ? await API.archivePractice(item.id)
        : await API.restorePractice(item.id);
    } else {
      res = await discoveryApi.archiveMemos([item.id], next);
    }
    // A falsy/null envelope is a swallowed failure (R2-Critical-1).
    if (!res || res.success === false) {
      console.error('[contentRowActions] Archive action failed:', res?.error);
      notifyArchiveFailed({ count: 1, type: item.type, archived: next });
      return;
    }
    item.archived = next;
    item.archivedAt = next ? new Date().toISOString() : null;
    if (item.payload) {
      // Threads track this toggle via `dismissedAt` (the system-exit
      // `archivedAt` field is a distinct, separately-checked value) and must
      // not be overwritten here.
      if (item.type === 'thread') item.payload.dismissedAt = item.archivedAt;
      else if (item.type === 'practice') item.payload.status = next ? 'archived' : 'active';
      else item.payload.archivedAt = item.archivedAt;
    }
    emitContentArchived(item.type, item.id, next);
    notifyArchived({ count: 1, type: item.type, archived: next });
  } catch (err) {
    console.error('[contentRowActions] Archive action failed:', err);
    notifyArchiveFailed({ count: 1, type: item.type, archived: next });
  }
}

/**
 * Mark a single non-session item viewed/unviewed, patching local state and
 * broadcasting on verified success only. Never called for `thread` items
 * (the menu item is gated out for them — see `getRowUnreadState`). Guides'
 * `payload` has no `viewedAt` field (`searchSources.js`'s `normalizeGuide`) —
 * `item.viewed` alone is authoritative there; session/note/practice items
 * also patch `item.payload.viewedAt` since `payload` is the raw record for
 * those three (`serializePractice` spreads `viewedAt` verbatim onto a
 * practice's payload).
 * @param {Object} item - SearchItem
 * @param {boolean} next - Desired viewed state
 * @returns {Promise<void>}
 */
export async function runMarkViewed(item, next) {
  try {
    let res;
    if (item.type === 'session') {
      res = await API.markSessionNoteViewed(item.id, next);
    } else if (item.type === 'guide') {
      res = await API.markGuideViewed(item.id, next);
    } else if (item.type === 'practice') {
      res = await API.markPracticeViewed(item.id, next);
    } else {
      res = await discoveryApi.markMemoViewed(item.id, next);
    }
    // A falsy/null envelope is a swallowed failure — never patch or
    // broadcast a mutation the server didn't confirm.
    if (!res || res.success === false) {
      console.error('[contentRowActions] Mark-viewed action failed:', res?.error);
      return;
    }
    item.viewed = next;
    if (item.payload && item.type !== 'guide') {
      item.payload.viewedAt = next ? new Date().toISOString() : null;
    }
    emitContentViewed(item.type, item.id, next);
  } catch (err) {
    console.error('[contentRowActions] Mark-viewed action failed:', err);
  }
}

/**
 * Toggle a session's manual unread state (session-only — see
 * `getRowUnreadState`; never called for note/guide/practice/thread items).
 * Different field, endpoint, and broadcast from `runMarkViewed` — kept as a
 * separate function rather than a branch inside it. Patches local state and
 * broadcasts on verified success only.
 * @param {Object} item - SearchItem (type === 'session')
 * @param {boolean} next - Desired flagged state
 * @returns {Promise<void>}
 */
export async function runToggleFlag(item, next) {
  try {
    const res = await API.flagSessionNote(item.id, next);
    // A falsy/null envelope is a swallowed failure — never patch or
    // broadcast a mutation the server didn't confirm.
    if (!res || res.success === false) {
      console.error('[contentRowActions] Flag action failed:', res?.error);
      return;
    }
    item.flagged = next;
    if (item.payload) item.payload.flaggedAt = next ? new Date().toISOString() : null;
    emitContentFlagged(item.type, item.id, next);
  } catch (err) {
    console.error('[contentRowActions] Flag action failed:', err);
  }
}

/**
 * Delete a single item and broadcast on verified success. Sessions delete by
 * the underlying Session's UUID (`payload.session.id` / `payload.sessionId`)
 * — NEVER `item.id`, which is the SessionNote row id, not the id
 * `API.deleteSession` expects (mirrors SessionsSection._fanDelete's exact
 * extraction). A 404 (`SESSION_NOT_FOUND`) is treated as already-gone
 * success. Practices use their UUID directly with `API.deletePractice` and
 * require its `{ deleted: true }` confirmation before success. A guide is
 * refused outright: no guide surface renders a Delete row
 * ({@link buildContentRowMenuItems}), and the fall-through branch below is the
 * memo delete, so the refusal has to be explicit.
 *
 * Unlike the other runners, this does NOT patch the item (there is no
 * "deleted" field on a SearchItem) — it returns whether the delete succeeded
 * so the caller can remove the item from its OWN list (`SidebarRecentsController`'s
 * `items` / `DashboardManager`'s `libraryItems`), the one piece that can't be
 * generalized here since each surface owns a differently-shaped array.
 * @param {Object} item - SearchItem
 * @returns {Promise<boolean>} True if the delete succeeded
 */
export async function runDelete(item) {
  // Guides have no delete path at all. The fall-through at the foot of this
  // chain is the memo delete, so this must be an explicit refusal rather than
  // an omitted branch.
  if (item.type === 'guide') return false;
  try {
    if (item.type === 'session') {
      const sessionId = item.payload?.session?.id || item.payload?.sessionId;
      const res = await API.deleteSession(sessionId);
      if (res?.success === false && res?.error?.code && res.error.code !== 'SESSION_NOT_FOUND') {
        console.error('[contentRowActions] Delete failed:', res.error);
        return false;
      }
    } else if (item.type === 'thread') {
      await API.deleteOpenThreads([item.id]);
    } else if (item.type === 'practice') {
      const res = await API.deletePractice(item.id);
      if (!res?.deleted) {
        console.error('[contentRowActions] Delete failed:', res?.error);
        return false;
      }
    } else {
      await discoveryApi.deleteMemos([item.id]);
    }
    emitContentDeleted(item.type, item.id);
    return true;
  } catch (err) {
    console.error('[contentRowActions] Delete failed:', err);
    return false;
  }
}

/**
 * Open a NEW chat session to discuss an item — routes through the exact same
 * mode/entryType each content type's OWN detail page uses for its "Discuss"
 * action, so a kebab's "Discuss"/"Report back" behaves identically to opening
 * the item natively and clicking Discuss there:
 *  - session → mirrors `SessionsSection.buildSessionActions().primaryButton`
 *    (an ADVICE `session_continuation` autoPrompt referencing the session).
 *  - note → the shared, page-agnostic `launchMemoDiscussAdvice` (memoEntryLauncher.js),
 *    the same helper `SessionsSection`/`PartnerNotesSection` call for a
 *    received note's "Discuss Note" button. Sent notes have no Discuss edge
 *    (see `buildContentRowMenuItems`), so this branch is never reached for
 *    one. The ADVICE `memo_entry` session this creates stamps the note's
 *    `viewedAt` server-side (`partnerMemoService.getForDiscussion`'s
 *    first-open stamp) — `launchMemoDiscussAdvice` resolves `true` only once
 *    that session is actually up, so awaiting it (not fire-and-forget, unlike
 *    the other branches) is a real confirmation signal, not an optimistic
 *    guess.
 *  - thread → mirrors `DiscoverySection.handleQuestionClick` (an ADVICE
 *    `openThread` entry referencing the thread).
 *  - guide → no branch: guides get no "Discuss" menu item (see
 *    `buildContentRowMenuItems`), so this dispatch is never reached for one.
 *  - practice → mirrors `PracticesSection.handleDiscuss` exactly (relabeled
 *    "Report back" in the menu — see `buildContentRowMenuItems`): a TREATMENT
 *    `checkin` subMode navigate, fire-and-forget like the session/thread
 *    branches (no server-side stamp to confirm before proceeding).
 * @param {Object} item - SearchItem
 * @returns {Promise<boolean>} True only when the item's local `viewed` state
 *   was just flipped (note branch) — the caller's signal that a re-render is
 *   actually needed, since every other branch only emits a fire-and-forget
 *   `navigate` event with nothing local to redraw.
 */
export async function discussContentItem(item) {
  if (!item) return false;
  const { type } = item;

  if (type === 'session') {
    const session = item.payload?.session;
    if (!session?.id) return false;
    const sessionNumber = session.sessionNumber ?? '?';
    const conversationTitle = item.payload?.notesJson?.title || 'this session';
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'ADVICE',
      force: true,
      autoPrompt: {
        text: `I'd like to continue our discussion from Session ${sessionNumber}: ${conversationTitle}.`,
        source: 'session_continuation',
        sourceSessionIds: [session.id]
      }
    });
  } else if (type === 'note') {
    const opened = await launchMemoDiscussAdvice({ memoId: item.id, senderName: item.payload?.partnerName || '' });
    if (opened && !item.viewed) {
      item.viewed = true;
      if (item.payload) item.payload.viewedAt = new Date().toISOString();
      emitContentViewed('note', item.id, true);
      return true;
    }
  } else if (type === 'thread') {
    const thread = item.payload || {};
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'ADVICE',
      force: true,
      openThreadId: item.id,
      openThread: {
        id: item.id,
        question: thread.question,
        context: thread.context,
        sessionId: thread.sessionId,
        observationId: thread.observationId || null
      }
    });
  } else if (type === 'practice') {
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'TREATMENT',
      subMode: 'checkin',
      practiceId: item.id
    });
  }
  return false;
}
